import { motion } from "framer-motion";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../ui/accordion";

const faqs = [
  {
    q: "How does telehealth actually work?",
    a: "Before each session Kathleen sends you a private Zoom for Healthcare link. You click 'Join your session' on the website, confirm a short HIPAA / GDPR consent, and the secure video room opens in a new tab. No app sign-up, no patient portal — just a browser, headphones, and a quiet room.",
  },
  {
    q: "Are you accepting new clients?",
    a: "Yes. Use the 'Book a Consult' button to request an appointment. Kathleen replies within 1–2 business days with available times, a brief intake form, and the first session details.",
  },
  {
    q: "Do you take insurance?",
    a: (
      <>
        Sessions are private pay. You will receive itemised receipts and a
        superbill on request — these can be submitted for out-of-network
        reimbursement in the USA, and for tax relief in Ireland (Med 2 form,
        where applicable). Current ranges are published on the{" "}
        <a href="/fees" className="underline text-[#739072]">
          fees page
        </a>
        , with the exact figure confirmed in your free 15-min discovery call.
      </>
    ),
  },
  {
    q: "How long is a session and how many will I need?",
    a: "Most adult sessions run 45–60 minutes. Course length varies: SPEAK OUT!® for Parkinson's runs as a defined 12-session course (typically 3×/week for 4 weeks); cognitive-communication therapy is typically 8–12 weeks; swallow management plans are reassessed every 4–6 weeks. Kathleen builds an honest, evidence-based plan with you at the first session.",
  },
  {
    q: "What is a swallow evaluation and do I need one?",
    a: "If eating or drinking has become difficult, painful, or you've experienced coughing, throat clearing or unexplained weight loss, a clinical swallow evaluation can identify the cause and guide safe, enjoyable mealtimes. Kathleen will refer for an instrumental study (FEES or VFSS) when one is clinically indicated.",
  },
  {
    q: "Can a family member or carer join the session?",
    a: "Absolutely — and it's often beneficial. Family training is a core part of therapy for stroke recovery, Parkinson's care and dysphagia management. Just let Kathleen know in advance.",
  },
  {
    q: "How is my information protected?",
    a: "Sessions run on Zoom for Healthcare under a Business Associate Agreement (BAA). Inquiry data is stored under HIPAA (USA) and GDPR (EU/Ireland) safeguards. We capture only what's needed, hash IPs in the audit trail, and never sell or share your data. See the Privacy Notice for full detail.",
  },
];

export const FAQ = () => {
  return (
    <section
      id="faq"
      data-testid="faq-section"
      className="relative py-20 md:py-32"
    >
      <div className="max-w-5xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end mb-12">
          <div className="lg:col-span-7">
            <p className="text-xs uppercase tracking-[0.25em] text-[#5C6661] mb-4">
              Frequently asked
            </p>
            <h2
              data-testid="faq-title"
              className="font-serif text-3xl md:text-4xl lg:text-5xl font-light tracking-tight text-[#2D332F] leading-tight"
            >
              Answers to the
              <br />
              <em className="text-[#739072]">most-asked questions.</em>
            </h2>
          </div>
          <p className="lg:col-span-5 text-[#5C6661] leading-relaxed">
            Can&rsquo;t find what you need? Reach out — Kathleen will reply
            personally.
          </p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((f, i) => (
              <AccordionItem
                key={f.q}
                value={`item-${i}`}
                data-testid={`faq-item-${i}`}
                className="border-b border-black/10"
              >
                <AccordionTrigger className="font-serif text-lg md:text-xl text-[#2D332F] py-6 text-left hover:no-underline">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-[#5C6661] text-base leading-relaxed pb-6 pr-6">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>
      </div>
    </section>
  );
};
